// components/shared/notifications/NotificationUnsubscribeManager.tsx
import React, { useEffect, useState } from "react";
import { Ban, RotateCcw, Trash2, UserX, BellOff } from "lucide-react";
import { notificationService } from "../../../../services/administration/notificationService";

interface UnsubscribeEntry {
  id: string;
  email: string;
  user_id?: string;
  source?: string;
  created_at?: string;
}

interface AlertProps {
  message: string;
  type: "success" | "error";
  onClose?: () => void;
}

const AlertBox: React.FC<AlertProps> = ({ message, type, onClose }) => (
  <div
    className={`p-4 mb-4 rounded-lg flex justify-between items-center ${
      type === "success" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
    }`}
  >
    <span className="text-sm font-medium">{message}</span>
    {onClose && (
      <button onClick={onClose} className="text-sm font-bold hover:opacity-70">
        ✕
      </button>
    )}
  </div>
);

export const NotificationUnsubscribeManager: React.FC = () => {
  const [entries, setEntries] = useState<UnsubscribeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [alert, setAlert] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const showAlert = (message: string, type: "success" | "error") => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const loadEntries = async () => {
    setLoading(true);
    try {
      const response = await notificationService.getUnsubscribedNotifications();
      console.log(response);
      setEntries(response.data || []);
    } catch (error: any) {
      showAlert(error.message || "Erreur lors du chargement", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEntries();
  }, []);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      await notificationService.addUnsubscribedNotification(newEmail.trim());
      showAlert("Utilisateur ajouté à la liste des désinscrits notifications", "success");
      setNewEmail("");
      await loadEntries();
    } catch (error: any) {
      showAlert(error.message || "Erreur lors de l'ajout", "error");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (entry: UnsubscribeEntry) => {
    if (!confirm(`Voulez-vous vraiment réactiver les notifications pour ${entry.email} ?`)) return;
    try {
      await notificationService.removeUnsubscribedNotification(entry.id);
      showAlert("Notifications réactivées avec succès", "success");
      await loadEntries();
    } catch (error: any) {
      showAlert(error.message || "Erreur lors de la suppression", "error");
    }
  };

  const sourceLabel = (source?: string) => {
    if (source === "mobile_preference") return "App mobile";
    if (source === "admin") return "Admin";
    return source || "admin";
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <UserX className="w-5 h-5 text-red-500" />
            Gestion des désinscrits notifications
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Ces utilisateurs ne recevront plus les notifications push ni les notifications in-app admin. Le toggle
            Préférences → Notifications de l&apos;app mobile alimente aussi cette liste.
          </p>
        </div>
        <button
          onClick={loadEntries}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
        >
          <RotateCcw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {alert && <AlertBox message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}

      <form onSubmit={handleAdd} className="flex gap-3 mb-6">
        <div className="flex-1">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Email à désinscrire des notifications"
            required
            className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        <button
          type="submit"
          disabled={adding}
          className="flex items-center gap-2 px-4 py-2 font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {adding ? (
            <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent" />
          ) : (
            <Ban className="w-4 h-4" />
          )}
          Désinscrire
        </button>
      </form>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="w-8 h-8 rounded-full border-4 border-green-500 animate-spin border-t-transparent" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <BellOff className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600 font-medium">Aucun utilisateur désinscrit</p>
          <p className="text-sm text-gray-500 mt-1">
            Les utilisateurs qui désactivent les notifications dans l&apos;app apparaîtront ici.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-left">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-sm font-medium text-gray-700">Email</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-700">Source</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-700">Date</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-700 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900 font-medium">{entry.email}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 capitalize">{sourceLabel(entry.source)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {entry.created_at ? new Date(entry.created_at).toLocaleString("fr-FR") : "-"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleRemove(entry)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
                    >
                      <Trash2 className="w-4 h-4" />
                      Réactiver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NotificationUnsubscribeManager;
